/* ==========================================================================
   Nova — JavaScript global
   ========================================================================== */

(() => {
  const theme = window.theme || {};
  const routes = window.routes || {};

  /* Utilidades ----------------------------------------------------------- */
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  const formatMoney = (cents, format = theme.moneyFormat || '${{amount}}') => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10) || 0;
    const fmt = (n, decimals, thousands = ',', decimal = '.') => {
      const fixed = (n / 100).toFixed(decimals);
      const [int, dec] = fixed.split('.');
      const intStr = int.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return dec ? intStr + decimal + dec : intStr;
    };
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return format;
    let value;
    switch (match[1]) {
      case 'amount_no_decimals': value = fmt(cents, 0); break;
      case 'amount_with_comma_separator': value = fmt(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fmt(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = fmt(cents, 2, "'", '.'); break;
      case 'amount_with_space_separator': value = fmt(cents, 2, ' ', ','); break;
      default: value = fmt(cents, 2);
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  };

  const toast = (message, ms = 2400) => {
    const el = $('.toast');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(el._t);
    el._t = setTimeout(() => (el.hidden = true), ms);
  };

  const lockScroll = (lock) => document.body.classList.toggle('overflow-hidden', lock);

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  window.Nova = { formatMoney, toast, debounce, parseHTML };

  /* Animaciones al hacer scroll ----------------------------------------- */
  let animObserver;
  const initAnimations = (root = document) => {
    if (!document.body.classList.contains('has-animations')) return;
    if (!('IntersectionObserver' in window)) {
      $$('[data-animate]', root).forEach((el) => el.classList.add('is-visible'));
      return;
    }
    if (!animObserver) {
      animObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              if (!theme.animationRepeat) animObserver.unobserve(entry.target);
            } else if (theme.animationRepeat) {
              entry.target.classList.remove('is-visible');
            }
          });
        },
        { rootMargin: '0px 0px -60px 0px', threshold: 0.05 }
      );
    }
    $$('[data-animate]', root).forEach((el) => {
      if (el._animated) return;
      el._animated = true;
      const siblings = Array.from(el.parentElement ? el.parentElement.children : []).filter((c) => c.hasAttribute('data-animate'));
      const index = siblings.indexOf(el);
      if (index > 0) el.style.setProperty('--i', Math.min(index, 8));
      animObserver.observe(el);
    });
  };

  /* Cajones laterales ---------------------------------------------------- */
  class SideDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-close]')) this.close();
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
    }
    open(opener) {
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      lockScroll(true);
      const focusable = this.querySelector('input, button:not([data-close]), a, [tabindex]');
      setTimeout(() => (this.querySelector('.drawer__panel') || this).focus({ preventScroll: true }), 50);
      if (focusable && this.dataset.autofocus) focusable.focus();
      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }
    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      if (!$('.drawer.is-open') && !$('modal-dialog:not([hidden])')) lockScroll(false);
      if (this.opener && this.opener.focus) this.opener.focus({ preventScroll: true });
    }
  }
  customElements.define('side-drawer', SideDrawer);

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (!opener) return;
    const drawer = document.getElementById(opener.dataset.drawerOpen);
    if (drawer && drawer.open) {
      e.preventDefault();
      drawer.open(opener);
    }
  });

  /* Modales -------------------------------------------------------------- */
  class ModalDialog extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-close]')) this.close();
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
    }
    open(opener) {
      this.opener = opener || document.activeElement;
      this.hidden = false;
      lockScroll(true);
      const content = this.querySelector('.modal__content');
      if (content) {
        content.setAttribute('tabindex', '-1');
        content.focus({ preventScroll: true });
      }
      this.dispatchEvent(new CustomEvent('modal:open'));
    }
    close() {
      this.hidden = true;
      $$('video', this).forEach((v) => v.pause());
      if (!$('.drawer.is-open') && !$('modal-dialog:not([hidden])')) lockScroll(false);
      if (this.opener && this.opener.focus) this.opener.focus({ preventScroll: true });
      this.dispatchEvent(new CustomEvent('modal:close'));
    }
  }
  customElements.define('modal-dialog', ModalDialog);

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-modal-open]');
    if (!opener) return;
    const modal = document.getElementById(opener.dataset.modalOpen);
    if (modal && modal.open) {
      e.preventDefault();
      modal.open(opener);
    }
  });

  /* Slider --------------------------------------------------------------- */
  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.slider = this.querySelector('.slider');
      if (!this.slider) return;
      this.slides = Array.from(this.slider.children);
      this.prev = this.querySelector('[data-slider-prev]');
      this.next = this.querySelector('[data-slider-next]');
      this.dots = $$('[data-slider-dot]', this);
      this.counter = this.querySelector('[data-slider-counter]');
      this.loop = this.dataset.loop !== undefined;

      this.prev && this.prev.addEventListener('click', () => this.go(-1));
      this.next && this.next.addEventListener('click', () => this.go(1));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));
      this.slider.addEventListener('scroll', debounce(() => this.update(), 60), { passive: true });
      this.update();

      const autoplay = parseFloat(this.dataset.autoplay);
      if (autoplay > 0) {
        this.autoplayMs = autoplay * 1000;
        this.play();
        this.addEventListener('mouseenter', () => this.pause());
        this.addEventListener('mouseleave', () => this.play());
        this.addEventListener('focusin', () => this.pause());
      }
    }
    get perView() {
      const first = this.slides[0];
      if (!first) return 1;
      return Math.max(1, Math.round(this.slider.clientWidth / first.getBoundingClientRect().width));
    }
    get index() {
      const first = this.slides[0];
      if (!first) return 0;
      const w = first.getBoundingClientRect().width + parseFloat(getComputedStyle(this.slider).columnGap || 0);
      return Math.round(this.slider.scrollLeft / w);
    }
    goTo(i) {
      const slide = this.slides[Math.max(0, Math.min(i, this.slides.length - 1))];
      if (!slide) return;
      this.slider.scrollTo({ left: slide.offsetLeft - this.slider.offsetLeft, behavior: 'smooth' });
    }
    go(dir) {
      const max = this.slides.length - this.perView;
      let target = this.index + dir;
      if (this.loop) {
        if (target > max) target = 0;
        if (target < 0) target = max;
      }
      this.goTo(target);
    }
    update() {
      const i = this.index;
      const max = this.slides.length - this.perView;
      if (!this.loop) {
        if (this.prev) this.prev.disabled = i <= 0;
        if (this.next) this.next.disabled = i >= max;
      }
      this.dots.forEach((d, n) => d.setAttribute('aria-current', n === i ? 'true' : 'false'));
      if (this.counter) this.counter.textContent = `${Math.min(i + 1, this.slides.length)} / ${this.slides.length}`;
      this.dispatchEvent(new CustomEvent('slider:change', { detail: { index: i, slide: this.slides[i] } }));
    }
    play() {
      this.pause();
      this.timer = setInterval(() => this.go(1), this.autoplayMs);
    }
    pause() {
      clearInterval(this.timer);
    }
    disconnectedCallback() {
      this.pause();
    }
  }
  customElements.define('slider-component', SliderComponent);

  /* Cantidad ------------------------------------------------------------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-qty]');
        if (!btn) return;
        e.preventDefault();
        const step = parseInt(this.input.step, 10) || 1;
        const min = parseInt(this.input.min, 10) || 0;
        const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
        let value = (parseInt(this.input.value, 10) || 0) + (btn.dataset.qty === 'plus' ? step : -step);
        value = Math.max(min, Math.min(max, value));
        if (String(value) === this.input.value) return;
        this.input.value = value;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* Header --------------------------------------------------------------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.sticky;
      this.wrapper = this.closest('.section-header-wrapper') || this.closest('.shopify-section');
      this.setHeights();
      window.addEventListener('resize', debounce(() => this.setHeights(), 150));
      if (this.mode === 'scroll-up') {
        this.lastY = window.scrollY;
        window.addEventListener('scroll', () => this.onScroll(), { passive: true });
      }
      // Menús desplegables: cerrar al hacer clic fuera y abrir al pasar el cursor
      $$('details', this).forEach((details) => {
        const summary = details.querySelector('summary');
        if (details.dataset.hover !== undefined) {
          details.addEventListener('mouseenter', () => { details.open = true; this.setHeights(); });
          details.addEventListener('mouseleave', () => (details.open = false));
        }
        details.addEventListener('toggle', () => {
          if (details.open) {
            $$('details[open]', this).forEach((d) => { if (d !== details && !d.contains(details)) d.open = false; });
            this.setHeights();
          }
        });
        summary && summary.addEventListener('keyup', (e) => { if (e.key === 'Escape') details.open = false; });
      });
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) $$('details[open]', this).forEach((d) => (d.open = false));
      });
    }
    setHeights() {
      const rect = this.getBoundingClientRect();
      document.documentElement.style.setProperty('--header-h', `${rect.height}px`);
      document.documentElement.style.setProperty('--header-bottom', `${Math.max(rect.bottom, 0)}px`);
    }
    onScroll() {
      const y = window.scrollY;
      const hidden = y > this.lastY && y > this.offsetHeight * 2;
      document.body.classList.toggle('header-hidden', hidden && !$('details[open]', this));
      this.lastY = y;
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* Búsqueda predictiva -------------------------------------------------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      if (!this.input || !this.results || this.dataset.enabled !== 'true') return;
      this.input.addEventListener('input', debounce(() => this.search(), 300));
    }
    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) {
        this.results.innerHTML = '';
        return;
      }
      this.abort && this.abort.abort();
      this.abort = new AbortController();
      try {
        const url = `${routes.predictive_search_url}?q=${encodeURIComponent(q)}&section_id=predictive-search&resources[type]=product,collection,article,page,query&resources[limit]=6`;
        const res = await fetch(url, { signal: this.abort.signal });
        if (!res.ok) return;
        const html = await res.text();
        const doc = parseHTML(html);
        const content = doc.querySelector('#shopify-section-predictive-search');
        this.results.innerHTML = content ? content.innerHTML : '';
      } catch (e) { /* abortado */ }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* Carrito -------------------------------------------------------------- */
  const Cart = {
    sectionIds() {
      return $$('[data-cart-root]').map((el) => el.dataset.cartRoot);
    },
    render(sections) {
      if (!sections) return;
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = parseHTML(html);
        const fresh = doc.querySelector(`[data-cart-root="${id}"]`);
        $$(`[data-cart-root="${id}"]`).forEach((el) => {
          if (fresh) el.innerHTML = fresh.innerHTML;
          if (fresh) Object.keys(fresh.dataset).forEach((k) => (el.dataset[k] = fresh.dataset[k]));
        });
      });
      const root = $('[data-cart-root]');
      if (root && root.dataset.count !== undefined) this.updateCount(root.dataset.count);
      initAnimations();
      document.dispatchEvent(new CustomEvent('cart:rendered'));
    },
    updateCount(count) {
      $$('.cart-count').forEach((el) => {
        el.textContent = count;
        el.dataset.count = count;
      });
    },
    async request(url, body) {
      const ids = this.sectionIds();
      const payload = Object.assign({}, body, ids.length ? { sections: ids.join(','), sections_url: window.location.pathname } : {});
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok || data.status) {
        const msg = data.description || data.message || theme.strings.cartError;
        throw new Error(typeof msg === 'string' ? msg : theme.strings.cartError);
      }
      return data;
    },
    async add(items) {
      const data = await this.request(`${routes.cart_add_url}.js`, { items });
      if (data.sections) this.render(data.sections);
      else await this.refresh();
      document.dispatchEvent(new CustomEvent('cart:added', { detail: data }));
      return data;
    },
    async change(line, quantity) {
      const data = await this.request(`${routes.cart_change_url}.js`, { line, quantity });
      this.render(data.sections);
      return data;
    },
    async update(body) {
      const data = await this.request(`${routes.cart_update_url}.js`, body);
      if (data.sections) this.render(data.sections);
      return data;
    },
    async refresh() {
      const ids = this.sectionIds();
      if (!ids.length) {
        const cart = await fetch(`${routes.cart_url}.js`).then((r) => r.json());
        this.updateCount(cart.item_count);
        return;
      }
      const res = await fetch(`${window.location.pathname}?sections=${ids.join(',')}`);
      this.render(await res.json());
    },
    open() {
      const drawer = document.getElementById('CartDrawer');
      if (drawer && theme.cartType === 'drawer') drawer.open();
      else window.location.href = routes.cart_url;
    }
  };
  window.Nova.Cart = Cart;

  // Controles de líneas del carrito (cajón y página)
  document.addEventListener('change', async (e) => {
    const input = e.target.closest('[data-cart-root] [data-line-qty]');
    if (!input) return;
    const item = input.closest('.cart-item');
    item && item.classList.add('is-loading');
    try {
      await Cart.change(parseInt(input.dataset.line, 10), parseInt(input.value, 10));
    } catch (err) {
      toast(err.message);
      item && item.classList.remove('is-loading');
    }
  });

  document.addEventListener('click', async (e) => {
    const remove = e.target.closest('[data-cart-root] [data-line-remove]');
    if (!remove) return;
    e.preventDefault();
    const item = remove.closest('.cart-item');
    item && item.classList.add('is-loading');
    try {
      await Cart.change(parseInt(remove.dataset.lineRemove, 10), 0);
    } catch (err) {
      toast(err.message);
      item && item.classList.remove('is-loading');
    }
  });

  // Nota del pedido
  document.addEventListener('input', debounce((e) => {
    const note = e.target.closest('[data-cart-note]');
    if (!note) return;
    fetch(`${routes.cart_update_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: note.value })
    });
  }, 500));

  // Código de descuento
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-discount-form]');
    if (!form) return;
    e.preventDefault();
    const code = form.querySelector('input').value.trim();
    if (!code) return;
    const existing = $$('[data-discount-code]').map((el) => el.dataset.discountCode);
    const codes = Array.from(new Set([...existing, code])).join(',');
    try {
      await Cart.update({ discount: codes });
    } catch (err) {
      toast(err.message);
    }
  });
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-discount-remove]');
    if (!btn) return;
    e.preventDefault();
    const remaining = $$('[data-discount-code]')
      .map((el) => el.dataset.discountCode)
      .filter((c) => c !== btn.dataset.discountRemove)
      .join(',');
    try {
      await Cart.update({ discount: remaining });
    } catch (err) {
      toast(err.message);
    }
  });

  // Casilla de términos y condiciones
  const syncTerms = () => {
    $$('[data-terms-checkbox]').forEach((box) => {
      const root = box.closest('[data-cart-root]') || document;
      $$('[name="checkout"]', root).forEach((btn) => (btn.disabled = !box.checked));
    });
  };
  document.addEventListener('change', (e) => { if (e.target.matches('[data-terms-checkbox]')) syncTerms(); });
  document.addEventListener('cart:rendered', syncTerms);

  // Regalo automático en el carrito
  let giftBusy = false;
  const syncGift = async () => {
    if (giftBusy) return;
    const el = $('[data-cart-gift]');
    if (!el) return;
    const variantId = parseInt(el.dataset.variant, 10);
    const threshold = parseInt(el.dataset.threshold, 10);
    const total = parseInt(el.dataset.total, 10);
    const giftLine = parseInt(el.dataset.line, 10) || 0;
    if (!variantId) return;
    giftBusy = true;
    try {
      if (total >= threshold && !giftLine) {
        await Cart.add([{ id: variantId, quantity: 1, properties: { _gift: 'true' } }]);
      } else if (total < threshold && giftLine) {
        await Cart.change(giftLine, 0);
      }
    } catch (err) { /* sin inventario */ }
    giftBusy = false;
  };
  document.addEventListener('cart:rendered', syncGift);

  // Temporizador de reserva del carrito
  const initCartTimers = () => {
    $$('[data-cart-timer]').forEach((el) => {
      if (el._timer) return;
      const minutes = parseInt(el.dataset.minutes, 10) || 10;
      const key = 'nova-cart-timer';
      let end = parseInt(sessionStorage.getItem(key), 10);
      if (!end || end < Date.now()) {
        end = Date.now() + minutes * 60000;
        try { sessionStorage.setItem(key, end); } catch (e) {}
      }
      const out = el.querySelector('[data-time]');
      const tick = () => {
        const left = Math.max(0, end - Date.now());
        const m = Math.floor(left / 60000);
        const s = Math.floor((left % 60000) / 1000);
        if (out) out.textContent = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
        if (left <= 0) {
          clearInterval(el._timer);
          if (el.dataset.expired) el.innerHTML = el.dataset.expired;
        }
      };
      tick();
      el._timer = setInterval(tick, 1000);
    });
  };
  document.addEventListener('cart:rendered', initCartTimers);

  /* Formulario de producto ---------------------------------------------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    async onSubmit(e) {
      e.preventDefault();
      const button = this.form.querySelector('[type="submit"][name="add"]') || this.form.querySelector('[type="submit"]');
      if (!button || button.getAttribute('aria-disabled') === 'true' || button.disabled) return;
      const errorBox = this.querySelector('[data-form-error]');
      if (errorBox) errorBox.hidden = true;

      // Validar campos personalizados obligatorios
      const formId = this.form.id;
      const required = formId ? $$(`[form="${formId}"][required]`) : [];
      for (const field of required) {
        if (!field.value.trim()) {
          field.reportValidity();
          return;
        }
      }

      button.classList.add('is-loading');
      button.setAttribute('aria-disabled', 'true');

      const fd = new FormData(this.form);
      const main = { id: parseInt(fd.get('id'), 10), quantity: parseInt(fd.get('quantity'), 10) || 1, properties: {} };
      for (const [k, v] of fd.entries()) {
        const m = k.match(/^properties\[(.+)\]$/);
        if (m && v !== '') main.properties[m[1]] = v;
      }
      if (fd.get('selling_plan')) main.selling_plan = fd.get('selling_plan');
      const items = [main];

      // Productos complementarios marcados (bloque upsell)
      if (formId) {
        $$(`[data-upsell-for="${formId}"]`).forEach((upsell) => {
          const toggle = upsell.querySelector('[data-upsell-toggle]');
          const select = upsell.querySelector('[data-upsell-variant]');
          if (toggle && toggle.checked && select && select.value) {
            items.push({ id: parseInt(select.value, 10), quantity: 1 });
          }
        });
      }

      try {
        await Cart.add(items);
        const modal = this.closest('modal-dialog');
        if (modal) modal.close();
        if (theme.cartType === 'drawer' && document.getElementById('CartDrawer')) {
          Cart.open();
        } else {
          window.location.href = routes.cart_url;
        }
      } catch (err) {
        if (errorBox) {
          errorBox.textContent = err.message;
          errorBox.hidden = false;
        } else {
          toast(err.message);
        }
      } finally {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-disabled');
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* Selector de variantes ------------------------------------------------ */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-variants]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.sectionId = this.dataset.section;
      this.addEventListener('change', () => this.onChange());
      this.updateAvailability();
    }
    get selectedOptions() {
      return $$('[data-option-index]', this)
        .sort((a, b) => a.dataset.optionIndex - b.dataset.optionIndex)
        .map((group) => {
          if (group.tagName === 'SELECT') return group.value;
          const checked = group.querySelector('input:checked');
          return checked ? checked.value : null;
        });
    }
    onChange() {
      const options = this.selectedOptions;
      $$('[data-option-index]', this).forEach((group, i) => {
        const label = group.querySelector('[data-selected-value]');
        if (label) label.textContent = options[i] || '';
      });
      const variant = this.variants.find((v) => v.options.every((o, i) => o === options[i]));
      this.updateAvailability();
      document.dispatchEvent(new CustomEvent('variant:change', { detail: { sectionId: this.sectionId, variant } }));
      if (variant && this.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }
    updateAvailability() {
      const selected = this.selectedOptions;
      $$('[data-option-index]', this).forEach((group) => {
        const idx = parseInt(group.dataset.optionIndex, 10);
        const inputs = group.tagName === 'SELECT' ? $$('option', group) : $$('input', group);
        inputs.forEach((input) => {
          const available = this.variants.some(
            (v) => v.available && v.options[idx] === input.value && v.options.every((o, i) => i === idx || i > idx || o === selected[i])
          );
          input.classList.toggle('is-disabled', !available);
          if (group.tagName === 'SELECT') {
            const base = input.dataset.label || input.textContent;
            input.dataset.label = base;
            input.textContent = available ? base : `${base} — ${theme.strings.soldOut}`;
          }
        });
      });
    }
  }
  customElements.define('variant-picker', VariantPicker);

  // Actualiza la información del producto al cambiar la variante
  const renderPriceHTML = (variant, el) => {
    const large = el.dataset.price === 'large';
    const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
    let html = `<div class="price${large ? ' price--large' : ''}${onSale ? ' price--on-sale' : ''}${variant.available ? '' : ' price--sold-out'}">`;
    html += `<span class="price__current">${formatMoney(variant.price)}</span>`;
    if (onSale) {
      html += `<s class="price__compare">${formatMoney(variant.compare_at_price)}</s>`;
      if (el.dataset.saveType) {
        const saved = variant.compare_at_price - variant.price;
        const amount = el.dataset.saveType === 'amount' ? formatMoney(saved) : `${Math.round((saved * 100) / variant.compare_at_price)}%`;
        html += `<span class="price__save badge color-${el.dataset.saveScheme}">${theme.strings.save.replace('[amount]', amount)}</span>`;
      }
    }
    html += '</div>';
    return html;
  };

  document.addEventListener('variant:change', (e) => {
    const { sectionId, variant } = e.detail;
    const scope = $$(`[data-section-scope="${sectionId}"]`);
    scope.forEach((root) => {
      // Inputs ocultos de variante
      $$('input[name="id"]', root).forEach((input) => {
        if (input.closest('[data-upsell-for]')) return;
        if (variant) input.value = variant.id;
      });
      // Precio
      $$('[data-price]', root).forEach((el) => {
        if (variant) el.innerHTML = renderPriceHTML(variant, el);
      });
      // Botones
      $$('[data-add-button]', root).forEach((btn) => {
        const label = btn.querySelector('[data-add-label]');
        const available = variant && variant.available;
        btn.disabled = !available;
        if (label) label.textContent = !variant ? theme.strings.unavailable : available ? (btn.dataset.label || theme.strings.addToCart) : theme.strings.soldOut;
      });
      // SKU
      $$('[data-sku]', root).forEach((el) => {
        el.hidden = !(variant && variant.sku);
        const v = el.querySelector('[data-sku-value]');
        if (v && variant) v.textContent = variant.sku || '';
      });
      // Inventario / urgencia
      $$('[data-inventory]', root).forEach((el) => {
        if (!variant) return;
        const qty = variant.inventory;
        const threshold = parseInt(el.dataset.threshold, 10) || 10;
        const fake = parseInt(el.dataset.fakeStock, 10);
        const count = qty != null && qty > 0 ? qty : fake || null;
        const text = el.querySelector('[data-inventory-text]');
        const dot = el.querySelector('.urgency__dot');
        const bar = el.querySelector('.progress span');
        if (!variant.available) {
          if (text) text.textContent = theme.strings.soldOut;
          dot && dot.classList.remove('urgency__dot--ok');
        } else if (count && count <= threshold) {
          if (text) text.textContent = (el.dataset.lowText || '').replace('[count]', count);
          dot && dot.classList.remove('urgency__dot--ok');
          if (bar) bar.parentElement.style.setProperty('--value', `${Math.max(8, (count / threshold) * 100)}%`);
        } else {
          if (text) text.textContent = el.dataset.inStockText || '';
          dot && dot.classList.add('urgency__dot--ok');
        }
      });
      // Precios de ofertas por cantidad
      $$('[data-qb-option]', root).forEach((opt) => {
        if (!variant) return;
        const qty = parseInt(opt.dataset.qty, 10) || 1;
        const discount = parseFloat(opt.dataset.discount) || 0;
        const fixed = parseInt(opt.dataset.fixed, 10) || 0;
        const compare = (variant.compare_at_price && variant.compare_at_price > variant.price ? variant.compare_at_price : variant.price) * qty;
        const full = variant.price * qty;
        const total = Math.max(0, Math.round(full * (1 - discount / 100)) - fixed);
        const p = opt.querySelector('[data-qb-price]');
        const c = opt.querySelector('[data-qb-compare]');
        const each = opt.querySelector('[data-qb-each]');
        if (p) p.textContent = formatMoney(total);
        if (c) {
          c.textContent = compare > total ? formatMoney(compare) : '';
        }
        if (each) each.textContent = formatMoney(Math.round(total / qty));
      });
      // Barra fija
      $$('[data-sticky-select]', root).forEach((sel) => { if (variant) sel.value = variant.id; });
    });
  });

  /* Galería de producto -------------------------------------------------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.sliderEl = this.querySelector('slider-component');
      this.thumbs = $$('[data-thumb]', this);
      this.thumbs.forEach((t) =>
        t.addEventListener('click', () => this.setActive(t.dataset.thumb))
      );
      this.addEventListener('slider:change', (e) => {
        const slide = e.detail.slide;
        if (!slide) return;
        this.markThumb(slide.dataset.mediaId);
        $$('video', this).forEach((v) => { if (!slide.contains(v) && !v.autoplay) v.pause(); });
      });
      this.sectionId = this.dataset.section;
      document.addEventListener('variant:change', (e) => {
        if (e.detail.sectionId !== this.sectionId || !e.detail.variant) return;
        const mediaId = e.detail.variant.featured_media_id;
        if (mediaId) this.setActive(String(mediaId));
        if (this.dataset.filter === 'true') this.filterByVariant(e.detail.variant);
      });
      // Zoom
      this.addEventListener('click', (e) => {
        const z = e.target.closest('[data-zoom]');
        if (!z) return;
        const modal = document.getElementById(`ZoomModal-${this.sectionId}`);
        if (!modal) return;
        const img = modal.querySelector('img');
        img.src = z.dataset.zoom;
        img.alt = z.dataset.alt || '';
        modal.open(z);
      });
    }
    setActive(mediaId) {
      const slides = $$('[data-media-id]', this.querySelector('.slider') || this);
      const index = slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (index < 0) return;
      if (this.sliderEl && this.sliderEl.goTo && getComputedStyle(this.sliderEl.slider).overflowX !== 'visible') {
        this.sliderEl.goTo(index);
      } else if (window.innerWidth >= 990) {
        slides[index].scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      this.markThumb(mediaId);
    }
    markThumb(mediaId) {
      this.thumbs.forEach((t) => {
        const active = t.dataset.thumb === String(mediaId);
        t.setAttribute('aria-current', active ? 'true' : 'false');
        if (active) t.parentElement.scrollTo({ left: t.offsetLeft - t.parentElement.offsetLeft - 40, top: t.offsetTop - t.parentElement.offsetTop - 40, behavior: 'smooth' });
      });
    }
    filterByVariant(variant) {
      // Muestra solo las imágenes cuyo texto alternativo contiene el valor de la opción
      const values = variant.options.map((o) => o.toLowerCase());
      const slides = $$('[data-media-id]', this.querySelector('.slider'));
      let any = false;
      slides.forEach((s) => {
        const alt = (s.dataset.alt || '').toLowerCase();
        const match = !alt || values.some((v) => alt.includes(v));
        s.hidden = !match;
        if (match && alt) any = true;
      });
      if (!any) slides.forEach((s) => (s.hidden = false));
      this.thumbs.forEach((t) => {
        const slide = slides.find((s) => s.dataset.mediaId === t.dataset.thumb);
        t.hidden = slide ? slide.hidden : false;
      });
    }
  }
  customElements.define('product-gallery', ProductGallery);

  /* Barra fija de agregar al carrito ------------------------------------ */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const target = document.getElementById(this.dataset.watch);
      if (!target || !('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver(([entry]) => {
        const below = entry.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', !entry.isIntersecting && below);
      });
      io.observe(target);
      const select = this.querySelector('[data-sticky-select]');
      if (select) {
        select.addEventListener('change', () => {
          // Sincroniza el selector principal con la variante elegida
          const picker = document.querySelector(`variant-picker[data-section="${this.dataset.section}"]`);
          if (!picker) return;
          const variant = picker.variants.find((v) => String(v.id) === select.value);
          if (!variant) return;
          variant.options.forEach((value, i) => {
            const group = picker.querySelector(`[data-option-index="${i}"]`);
            if (!group) return;
            if (group.tagName === 'SELECT') group.value = value;
            else {
              const input = $$('input', group).find((inp) => inp.value === value);
              if (input) input.checked = true;
            }
          });
          picker.onChange();
        });
      }
      const btn = this.querySelector('[data-sticky-submit]');
      if (btn) {
        btn.addEventListener('click', () => {
          const form = document.getElementById(this.dataset.form);
          if (form) form.requestSubmit ? form.requestSubmit() : form.submit();
        });
      }
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* Ofertas por cantidad ------------------------------------------------- */
  class QuantityBreaks extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (e) => {
        const opt = e.target.closest('[data-qb-option]');
        if (!opt) return;
        const qty = opt.dataset.qty;
        const form = document.getElementById(this.dataset.form);
        if (!form) return;
        let input = form.querySelector('input[name="quantity"]');
        const external = document.querySelector(`input[name="quantity"][form="${this.dataset.form}"]`);
        if (external) input = external;
        if (input) {
          input.value = qty;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
      const checked = this.querySelector('input:checked');
      if (checked) checked.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  customElements.define('quantity-breaks', QuantityBreaks);

  // Regalos por cantidad
  document.addEventListener('change', (e) => {
    const input = e.target;
    if (input.name !== 'quantity') return;
    const formId = input.getAttribute('form') || (input.form && input.form.id);
    if (!formId) return;
    const qty = parseInt(input.value, 10) || 1;
    $$(`[data-gifts-for="${formId}"] [data-gift-qty]`).forEach((g) => {
      g.classList.toggle('is-unlocked', qty >= parseInt(g.dataset.giftQty, 10));
    });
  });

  /* Fecha estimada de entrega ------------------------------------------- */
  const addBusinessDays = (date, days, skipWeekends) => {
    const d = new Date(date);
    let added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      if (!skipWeekends || (d.getDay() !== 0 && d.getDay() !== 6)) added++;
    }
    return d;
  };
  const initDeliveryDates = (root = document) => {
    $$('[data-delivery]', root).forEach((el) => {
      const locale = document.documentElement.lang || 'es';
      const skip = el.dataset.skipWeekends === 'true';
      const fmt = { weekday: el.dataset.weekday === 'true' ? 'short' : undefined, day: 'numeric', month: 'short' };
      const now = new Date();
      $$('[data-days]', el).forEach((out) => {
        const d = addBusinessDays(now, parseInt(out.dataset.days, 10) || 0, skip);
        out.textContent = d.toLocaleDateString(locale, fmt);
      });
    });
  };

  /* Cuenta regresiva ----------------------------------------------------- */
  const initCountdowns = (root = document) => {
    $$('[data-countdown]', root).forEach((el) => {
      if (el._cd) return;
      let end;
      if (el.dataset.end) {
        end = new Date(el.dataset.end).getTime();
      } else {
        const minutes = parseInt(el.dataset.minutes, 10) || 60;
        const key = `nova-cd-${el.dataset.countdown}`;
        end = parseInt(localStorage.getItem(key), 10);
        if (!end || end < Date.now()) {
          end = Date.now() + minutes * 60000;
          try { localStorage.setItem(key, end); } catch (e) {}
        }
      }
      const parts = { d: el.querySelector('[data-d]'), h: el.querySelector('[data-h]'), m: el.querySelector('[data-m]'), s: el.querySelector('[data-s]') };
      const tick = () => {
        const left = Math.max(0, end - Date.now());
        const d = Math.floor(left / 86400000);
        const h = Math.floor((left % 86400000) / 3600000);
        const m = Math.floor((left % 3600000) / 60000);
        const s = Math.floor((left % 60000) / 1000);
        if (parts.d) parts.d.textContent = String(d).padStart(2, '0');
        if (parts.h) parts.h.textContent = String(parts.d ? h : h + d * 24).padStart(2, '0');
        if (parts.m) parts.m.textContent = String(m).padStart(2, '0');
        if (parts.s) parts.s.textContent = String(s).padStart(2, '0');
        if (left <= 0) {
          clearInterval(el._cd);
          if (el.dataset.hideOnEnd === 'true') (el.closest('[data-countdown-wrap]') || el).hidden = true;
        }
      };
      tick();
      el._cd = setInterval(tick, 1000);
    });
  };

  /* Copiar código / compartir ------------------------------------------- */
  document.addEventListener('click', async (e) => {
    const copy = e.target.closest('[data-copy]');
    if (copy) {
      e.preventDefault();
      try {
        await navigator.clipboard.writeText(copy.dataset.copy);
        toast(copy.dataset.copiedText || theme.strings.copied);
      } catch (err) { /* sin permiso */ }
      return;
    }
    const share = e.target.closest('[data-share]');
    if (share) {
      e.preventDefault();
      const data = { title: document.title, url: share.dataset.share };
      if (navigator.share) {
        try { await navigator.share(data); } catch (err) {}
      } else {
        try {
          await navigator.clipboard.writeText(data.url);
          toast(theme.strings.copied);
        } catch (err) {}
      }
    }
  });

  /* Paquete de productos ------------------------------------------------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-bundle-add]');
    if (!btn) return;
    e.preventDefault();
    const items = btn.dataset.bundleAdd.split(',').filter(Boolean).map((id) => ({ id: parseInt(id, 10), quantity: 1 }));
    if (!items.length) return;
    btn.classList.add('is-loading');
    try {
      await Cart.add(items);
      Cart.open();
    } catch (err) {
      toast(err.message);
    } finally {
      btn.classList.remove('is-loading');
    }
  });

  /* Agregado rápido ------------------------------------------------------ */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    const modal = document.getElementById('QuickAddModal');
    if (!modal) return;
    btn.classList.add('is-loading');
    try {
      const url = btn.dataset.quickAdd.split('?')[0];
      const res = await fetch(`${url}?section_id=quick-add`);
      const doc = parseHTML(await res.text());
      const content = doc.querySelector('[data-quick-add-content]');
      const body = modal.querySelector('[data-quick-add-body]');
      body.innerHTML = '';
      if (content) {
        // Reinsertar nodos para que los custom elements se inicialicen
        body.appendChild(document.importNode(content, true));
      }
      modal.open(btn);
      initDeliveryDates(body);
    } catch (err) {
      window.location.href = btn.dataset.quickAdd;
    } finally {
      btn.classList.remove('is-loading');
    }
  });

  /* Recomendaciones de productos ---------------------------------------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      const load = async () => {
        try {
          const res = await fetch(this.dataset.url);
          const doc = parseHTML(await res.text());
          const fresh = doc.querySelector(`product-recommendations[data-key="${this.dataset.key}"]`);
          if (fresh && fresh.innerHTML.trim()) {
            this.innerHTML = fresh.innerHTML;
            initAnimations(this);
          } else if (this.dataset.hideEmpty !== undefined) {
            const section = this.closest('[data-hide-when-empty]') || this;
            section.hidden = true;
          }
        } catch (e) {}
      };
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting) {
            io.disconnect();
            load();
          }
        }, { rootMargin: '0px 0px 400px 0px' });
        io.observe(this);
      } else {
        load();
      }
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* Pestañas ------------------------------------------------------------- */
  class TabsComponent extends HTMLElement {
    connectedCallback() {
      this.buttons = $$('[role="tab"]', this);
      this.panels = $$('[role="tabpanel"]', this);
      this.buttons.forEach((btn, i) => {
        btn.addEventListener('click', () => this.select(i));
        btn.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') this.select((i + 1) % this.buttons.length, true);
          if (e.key === 'ArrowLeft') this.select((i - 1 + this.buttons.length) % this.buttons.length, true);
        });
      });
    }
    select(i, focus) {
      this.buttons.forEach((b, n) => {
        b.setAttribute('aria-selected', n === i ? 'true' : 'false');
        b.tabIndex = n === i ? 0 : -1;
      });
      this.panels.forEach((p, n) => (p.hidden = n !== i));
      if (focus) this.buttons[i].focus();
      initAnimations(this.panels[i]);
    }
  }
  customElements.define('tabs-component', TabsComponent);

  /* Comparador antes/después -------------------------------------------- */
  class CompareSlider extends HTMLElement {
    connectedCallback() {
      const input = this.querySelector('input[type="range"]');
      const box = this.querySelector('.compare-slider');
      if (!input || !box) return;
      const set = () => box.style.setProperty('--pos', `${input.value}%`);
      input.addEventListener('input', set);
      set();
    }
  }
  customElements.define('compare-slider', CompareSlider);

  /* Video ---------------------------------------------------------------- */
  class VideoPlayer extends HTMLElement {
    connectedCallback() {
      const play = this.querySelector('[data-video-play]');
      const tpl = this.querySelector('template');
      if (play && tpl) {
        play.addEventListener('click', () => {
          const wrap = this.querySelector('.video-wrap');
          wrap.appendChild(tpl.content.cloneNode(true));
          play.remove();
          const poster = wrap.querySelector('[data-poster]');
          poster && poster.remove();
          const video = wrap.querySelector('video');
          video && video.play();
        });
      }
      const video = this.querySelector('video');
      if (!video) return;
      const toggle = this.querySelector('[data-video-toggle]');
      const mute = this.querySelector('[data-video-mute]');
      const bar = this.querySelector('.video-progress span');
      toggle && toggle.addEventListener('click', () => {
        video.paused ? video.play() : video.pause();
      });
      video.addEventListener('play', () => toggle && toggle.classList.add('is-playing'));
      video.addEventListener('pause', () => toggle && toggle.classList.remove('is-playing'));
      mute && mute.addEventListener('click', () => {
        video.muted = !video.muted;
        mute.classList.toggle('is-muted', video.muted);
      });
      bar && video.addEventListener('timeupdate', () => {
        bar.style.width = `${(video.currentTime / video.duration) * 100 || 0}%`;
      });
    }
  }
  customElements.define('video-player', VideoPlayer);

  /* Historias ------------------------------------------------------------ */
  class StoriesViewer extends HTMLElement {
    connectedCallback() {
      this.items = $$('[data-story]', this);
      this.viewer = this.querySelector('.story-viewer');
      if (!this.viewer) return;
      document.body.appendChild(this.viewer);
      this.items.forEach((item, i) => item.addEventListener('click', () => this.show(i)));
      this.viewer.addEventListener('click', (e) => {
        if (e.target.closest('[data-story-close]')) this.close();
        if (e.target.closest('[data-story-prev]')) this.show(this.index - 1);
        if (e.target.closest('[data-story-next]')) this.show(this.index + 1);
      });
      document.addEventListener('keydown', (e) => {
        if (this.viewer.hidden) return;
        if (e.key === 'Escape') this.close();
        if (e.key === 'ArrowRight') this.show(this.index + 1);
        if (e.key === 'ArrowLeft') this.show(this.index - 1);
      });
      this.duration = (parseInt(this.dataset.duration, 10) || 5) * 1000;
    }
    show(i) {
      if (i < 0) i = 0;
      if (i >= this.items.length) return this.close();
      this.index = i;
      const item = this.items[i];
      const frame = this.viewer.querySelector('[data-story-media]');
      const cta = this.viewer.querySelector('[data-story-cta]');
      const bars = $$('.story-viewer__bars i', this.viewer);
      bars.forEach((b, n) => { b.style.transition = 'none'; b.style.width = n < i ? '100%' : '0'; });
      clearTimeout(this.timer);
      const src = item.dataset.video || item.dataset.image;
      frame.innerHTML = item.dataset.video
        ? `<video src="${src}" autoplay playsinline muted></video>`
        : `<img src="${src}" alt="">`;
      if (cta) {
        cta.hidden = !item.dataset.link;
        cta.href = item.dataset.link || '#';
        cta.textContent = item.dataset.label || '';
      }
      this.viewer.hidden = false;
      lockScroll(true);
      const video = frame.querySelector('video');
      const run = (ms) => {
        requestAnimationFrame(() => {
          bars[i].style.transition = `width ${ms}ms linear`;
          bars[i].style.width = '100%';
        });
        this.timer = setTimeout(() => this.show(i + 1), ms);
      };
      if (video) {
        video.addEventListener('loadedmetadata', () => run((video.duration || 5) * 1000), { once: true });
      } else {
        run(this.duration);
      }
    }
    close() {
      clearTimeout(this.timer);
      this.viewer.hidden = true;
      this.viewer.querySelector('[data-story-media]').innerHTML = '';
      lockScroll(false);
    }
  }
  customElements.define('stories-viewer', StoriesViewer);

  /* Imagen comprable ----------------------------------------------------- */
  document.addEventListener('click', (e) => {
    const dot = e.target.closest('.hotspot__dot');
    $$('.hotspot.is-open').forEach((h) => { if (!dot || h !== dot.parentElement) h.classList.remove('is-open'); });
    if (dot) dot.parentElement.classList.toggle('is-open');
  });

  /* Popup promocional ---------------------------------------------------- */
  class PromoPopup extends HTMLElement {
    connectedCallback() {
      this.modal = this.querySelector('modal-dialog');
      if (!this.modal) return;
      const key = `nova-popup-${this.dataset.id}`;
      const days = parseFloat(this.dataset.days) || 0;
      const test = this.dataset.test === 'true';
      let last = 0;
      try { last = parseInt(localStorage.getItem(key), 10) || 0; } catch (e) {}
      if (!test && last && Date.now() - last < days * 86400000) return;
      if (this.dataset.trigger === 'exit') {
        const onLeave = (e) => {
          if (e.clientY <= 0) {
            document.removeEventListener('mouseout', onLeave);
            this.show(key);
          }
        };
        document.addEventListener('mouseout', onLeave);
        setTimeout(() => this.show(key), 30000);
      } else {
        setTimeout(() => this.show(key), (parseInt(this.dataset.delay, 10) || 5) * 1000);
      }
    }
    show(key) {
      if (this.shown) return;
      this.shown = true;
      this.modal.open();
      try { localStorage.setItem(key, Date.now()); } catch (e) {}
    }
  }
  customElements.define('promo-popup', PromoPopup);

  /* Botón volver arriba -------------------------------------------------- */
  class ScrollTop extends HTMLElement {
    connectedCallback() {
      const btn = this.querySelector('button');
      const ring = this.querySelector('circle[data-ring]');
      const length = ring ? 2 * Math.PI * ring.r.baseVal.value : 0;
      if (ring) ring.style.strokeDasharray = length;
      const onScroll = () => {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        const p = max > 0 ? window.scrollY / max : 0;
        btn.classList.toggle('is-visible', window.scrollY > 400);
        if (ring) ring.style.strokeDashoffset = length * (1 - p);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      btn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
      onScroll();
    }
  }
  customElements.define('scroll-top', ScrollTop);

  /* Parallax ------------------------------------------------------------- */
  const parallaxEls = () => $$('[data-parallax]');
  const onParallax = () => {
    parallaxEls().forEach((el) => {
      const rect = el.parentElement.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;
      const speed = parseFloat(el.dataset.parallax) || 0.2;
      const offset = (rect.top + rect.height / 2 - window.innerHeight / 2) * speed;
      el.style.transform = `translate3d(0, ${offset}px, 0)`;
    });
  };
  if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    window.addEventListener('scroll', () => requestAnimationFrame(onParallax), { passive: true });
  }

  /* Reproductor de audio ------------------------------------------------- */
  class AudioPlayer extends HTMLElement {
    connectedCallback() {
      const audio = this.querySelector('audio');
      const btn = this.querySelector('[data-audio-toggle]');
      if (!audio || !btn) return;
      btn.addEventListener('click', () => (audio.paused ? audio.play() : audio.pause()));
      audio.addEventListener('play', () => btn.classList.add('is-playing'));
      audio.addEventListener('pause', () => btn.classList.remove('is-playing'));
    }
  }
  customElements.define('audio-player', AudioPlayer);

  /* Formularios en ventana (newsletter en popup, etc.) ------------------ */
  // Abre automáticamente el popup si el formulario devolvió éxito
  const reopenOnPosted = () => {
    $$('[data-open-if-posted]').forEach((el) => {
      const modal = el.closest('modal-dialog');
      if (modal) modal.open();
    });
  };

  /* Inicio --------------------------------------------------------------- */
  const init = (root = document) => {
    initAnimations(root);
    initDeliveryDates(root);
    initCountdowns(root);
    initCartTimers();
    syncTerms();
    onParallax();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => { init(); reopenOnPosted(); });
  else { init(); reopenOnPosted(); }

  document.addEventListener('shopify:section:load', (e) => init(e.target));
  window.Nova.init = init;
})();
